import { hasAnyRole, RoleType } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import { AppDispatch } from '../store';

// Generate a short error reference for log correlation
const generateErrorRef = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `ERR-${timestamp}-${random}`.toUpperCase();
};

export type NotificationLevel = 'success' | 'info' | 'warning' | 'error';

export type CloseReason = 'timeout' | 'maxsnack' | 'instructed';
export interface NotificationObject {
  message: string;
  level: NotificationLevel;
  options: {
    onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void;
    variant: NotificationLevel;
    key: string;
  };
  dismissed?: boolean;
}

const initialState = {
  notifications: [] as NotificationObject[],
};

export type NotificationState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    add(state, action: PayloadAction<NotificationObject>) {
      if (state.notifications.some((n) => n.level === action.payload.level && n.message === action.payload.message)) {
        return state;
      }
      if (state.notifications.some((m) => m.options?.key === action.payload.options?.key)) {
        return {
          ...state,
          notifications: state.notifications.map((m) =>
            m.options?.key === action.payload.options?.key ? action.payload : m,
          ),
        };
      }
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    },
    remove(state, action) {
      return {
        ...state,
        notifications: state.notifications.filter((n) => n.options.key !== action.payload),
      };
    },
    close(state, action) {
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.options.key === action.payload ? { ...n, dismissed: true } : { ...n },
        ),
      };
    },
    closeAll(state) {
      return {
        ...state,
        notifications: state.notifications.map((n) => ({ ...n, dismissed: true })),
      };
    },
    clear: () => initialState,
  },
});

export const addNotification =
  (
    message: string,
    level: NotificationLevel,
    onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void,
  ) =>
  (dispatch: AppDispatch) => {
    const notification: NotificationObject = {
      message,
      level,
      options: {
        onClose,
        variant: level,
        key: `${Date.now()}-${Math.random()}`,
      },
    };
    dispatch(slice.actions.add(notification));
  };

export const addExceptionNotification =
  (e: any, message?: string, onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void) =>
  (
    dispatch: AppDispatch,
    getState: () => { notification: NotificationState; auth: { account?: { roles?: RoleType[] } } },
  ) => {
    if (e.type === 'MessageException') {
      if (!getState().notification.notifications.some((n) => n.options?.key === e.id)) {
        dispatch(
          slice.actions.add({
            message: e.message,
            level: e.level,
            options: {
              onClose,
              variant: e.level,
              key: e.id,
            },
          }),
        );
      }
      return;
    }

    if (e.type === 'IdentifiableApiException') {
      dispatch(
        slice.actions.add({
          message: message ?? e.message,
          level: e.level ?? 'error',
          options: {
            onClose,
            variant: e.level ?? 'error',
            key: e.id ?? `${Date.now()}-${Math.random()}`,
          },
        }),
      );
      return;
    }

    // Generate error reference for log correlation
    const errorRef = generateErrorRef();

    // Try to parse the raw response string (NSwag ApiException stores raw JSON in e.response)
    const parsedResponse = (() => {
      try {
        return typeof e?.response === 'string' ? JSON.parse(e.response) : null;
      } catch {
        return null;
      }
    })();

    // Extract technical error message
    const technicalMessage =
      parsedResponse?.extensions?.message ??
      parsedResponse?.detail ??
      e?.response?.data?.message ??
      e?.response?.data?.title ??
      (typeof e?.response?.data === 'string' ? e?.response?.data : null) ??
      e?.message ??
      null;

    // Log full error with reference for support lookup
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${errorRef}]`, e);
    } else {
      Sentry.captureException(e, { tags: { errorRef } });
    }

    // Check if user is admin
    const roles = getState().auth?.account?.roles;
    const isAdmin = hasAnyRole(roles, [RoleType.ADMIN]);

    // Build user-facing message
    let userMessage: string;
    if (isAdmin && technicalMessage) {
      // Admins see short technical message with reference
      userMessage = `${technicalMessage} [${errorRef}]`;
    } else {
      // Regular users see friendly message with reference
      userMessage = `${message ?? 'An error occurred.'} [${errorRef}]`;
    }

    dispatch(
      slice.actions.add({
        message: userMessage,
        level: 'error',
        options: {
          onClose,
          variant: 'error',
          key: errorRef,
        },
      }),
    );
  };

export const addError = (
  message: string,
  onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void,
) => addNotification(message, 'error', onClose);

export const addInfo = (
  message: string,
  onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void,
) => addNotification(message, 'info', onClose);

export const addWarning = (
  message: string,
  onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void,
) => addNotification(message, 'warning', onClose);

export const addSuccess = (
  message: string,
  onClose?: (event: React.SyntheticEvent<any, Event>, reason: CloseReason) => void,
) => addNotification(message, 'success', onClose);

export const notificationReducer = slice.reducer;
export const notificationActions = slice.actions;
